import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { applyBranding } from "@/lib/branding";
import { toast } from "sonner";
import { formatGBP, formatUKDate } from "@/lib/format";
import { useSearchParams } from "react-router-dom";

const TABS = ["Branding", "Subscription", "Account"];

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "subscription" ? "Subscription" : "Branding";
  const [tab, setTab] = useState(initialTab);
  const { business, setBusiness } = useAuth();
  const [form, setForm] = useState({
    business_name: "", primary_color: "#1E90E6", secondary_color: "#F2A13A", accent_color: "#0B2545",
    logo_url: "", payment_message: "", phone: "", address: "",
  });

  useEffect(() => {
    if (business) {
      setForm({
        business_name: business.branding?.business_name || business.name || "",
        primary_color: business.branding?.primary_color || "#1E90E6",
        secondary_color: business.branding?.secondary_color || "#F2A13A",
        accent_color: business.branding?.accent_color || "#0B2545",
        logo_url: business.branding?.logo_url || "",
        payment_message: business.branding?.payment_message || "",
        phone: business.phone || "",
        address: business.address || "",
      });
    }
  }, [business]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/business/branding", form);
      setBusiness(data);
      applyBranding(data.branding);
      toast.success("Branding saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="settings-page">
      <h1 className="font-display text-3xl tracking-tight font-bold">Settings</h1>
      <div className="mt-4 inline-flex rounded-md border border-border bg-card p-1 text-sm">
        {TABS.map((t) => (
          <button key={t} onClick={() => { setTab(t); setParams({ tab: t.toLowerCase() }); }} className={`px-3 py-1.5 rounded ${tab === t ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`settings-tab-${t.toLowerCase()}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "Branding" && (
          <form onSubmit={save} className="grid md:grid-cols-2 gap-6" data-testid="branding-form">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Business name</label>
                <input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="branding-business-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Logo URL</label>
                <input type="url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…/logo.png" className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="branding-logo-url" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <ColorField label="Primary" value={form.primary_color} onChange={(v) => setForm({ ...form, primary_color: v })} testid="branding-primary" />
                <ColorField label="Secondary" value={form.secondary_color} onChange={(v) => setForm({ ...form, secondary_color: v })} testid="branding-secondary" />
                <ColorField label="Accent" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} testid="branding-accent" />
              </div>
              <div>
                <label className="text-sm font-medium">Business phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="branding-phone" />
              </div>
              <div>
                <label className="text-sm font-medium">Business address</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="branding-address" />
              </div>
              <div>
                <label className="text-sm font-medium">Payment page message</label>
                <textarea rows={3} value={form.payment_message} onChange={(e) => setForm({ ...form, payment_message: e.target.value })} className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm bg-transparent" data-testid="branding-payment-message" />
              </div>
              <button type="submit" className="rounded-md bg-primary text-primary-foreground px-5 py-2.5 font-medium" data-testid="branding-save">Save branding</button>
            </div>

            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Payment page preview</div>
              <div className="mt-4 rounded-xl p-6 shadow-sm" style={{ background: `linear-gradient(180deg, ${form.primary_color}12 0%, white 40%)` }}>
                <div className="flex items-center gap-3">
                  {form.logo_url ? <img src={form.logo_url} alt="" className="h-10 w-10 rounded-md object-cover" /> :
                    <div className="h-10 w-10 rounded-md grid place-items-center text-white font-bold" style={{ background: form.primary_color }}>{(form.business_name || "W")[0]}</div>}
                  <div className="font-display font-bold text-lg">{form.business_name || "Your Business"}</div>
                </div>
                <div className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Amount due</div>
                <div className="font-display text-4xl font-bold">£45.00</div>
                {form.payment_message && (
                  <div className="mt-4 rounded p-3 text-sm" style={{ background: form.secondary_color + "20", borderLeft: `3px solid ${form.secondary_color}` }}>
                    {form.payment_message}
                  </div>
                )}
                <button type="button" className="mt-4 w-full rounded-md px-4 py-3 font-semibold text-white" style={{ background: form.primary_color }}>Pay securely</button>
              </div>
            </div>
          </form>
        )}

        {tab === "Subscription" && <SubscriptionTab />}
        {tab === "Account" && <AccountTab />}
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded-md border border-border" data-testid={`${testid}-picker`} />
        <input value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 rounded-md border border-input px-2 py-1.5 text-xs font-mono bg-transparent" data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}

function SubscriptionTab() {
  const { business } = useAuth();
  const sub = business?.subscription || {};
  const [busy, setBusy] = useState(false);

  const upgrade = async (plan) => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/subscriptions/checkout", { plan, origin_url: origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const plans = [
    { id: "basic", name: "Basic", price: 10, items: ["1 user", "100 customers"] },
    { id: "pro", name: "Pro", price: 20, items: ["3 users", "Unlimited"], featured: true },
    { id: "team", name: "Team", price: 35, items: ["5+ users", "Unlimited"] },
  ];

  return (
    <div data-testid="subscription-tab">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Current plan</div>
            <div className="mt-1 font-display text-2xl font-bold capitalize">{sub.plan || "—"}</div>
            <div className="text-sm text-muted-foreground mt-1">
              Status: <span className="font-medium">{sub.status}</span> · Renews/ends {sub.current_period_end ? formatUKDate(sub.current_period_end) : "—"}
            </div>
          </div>
          {sub.status === "trialing" && <div className="rounded-md bg-amber-100 border border-amber-200 text-amber-900 px-3 py-2 text-sm">Free trial · upgrade anytime.</div>}
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`rounded-xl border p-6 ${p.featured ? "border-primary bg-primary/5" : "border-border bg-card"}`} data-testid={`sub-plan-${p.id}`}>
            <div className="font-display text-xl font-bold">{p.name}</div>
            <div className="mt-2 font-display text-3xl font-bold">£{p.price}<span className="text-sm text-muted-foreground font-medium">/mo</span></div>
            <ul className="mt-3 text-sm space-y-1 text-muted-foreground">
              {p.items.map((i) => <li key={i}>• {i}</li>)}
            </ul>
            <button disabled={busy} onClick={() => upgrade(p.id)} className="mt-4 w-full rounded-md bg-primary text-primary-foreground py-2 text-sm font-medium disabled:opacity-50" data-testid={`upgrade-to-${p.id}`}>
              {sub.plan === p.id ? "Renew/extend" : "Upgrade"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountTab() {
  const { user } = useAuth();
  return (
    <div className="rounded-xl border border-border bg-card p-6 max-w-md" data-testid="account-tab">
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Your account</div>
      <div className="mt-3 space-y-1">
        <div className="font-display text-xl font-bold">{user?.first_name} {user?.last_name}</div>
        <div className="text-muted-foreground text-sm">{user?.email}</div>
        <div className="text-muted-foreground text-sm capitalize">Role: {user?.role}</div>
      </div>
    </div>
  );
}
